import axios from "axios";
import store from "../store";

export const interceptorsSetup = () => {
  axios.interceptors.request.use(
    config => {
      let userData = JSON.parse(localStorage.getItem("userData"));
      if (!config.url.includes("/login/access-token")) {
        config.headers["Accept"] = "application/json";
        // "/file" downloads carry ?token= instead, except email attachments, which now need the header.
        const sendsToken =
          !config.url.includes("/file") || config.url.includes("/communication/email/file");
        if (userData && userData["access_token"] && sendsToken) {
          config.headers["Authorization"] = `Bearer ${userData["access_token"]}`;
        }
        if (config.url.includes("/login/refresh-token")) {
          delete config.headers["Authorization"];
        }
      } else {
        config.headers["Content-Type"] = "multipart/form-data";
      }
      return Promise.resolve(config);
    },
    error => Promise.reject(error)
  );
};

let isRefreshing = false;
let subscribers = [];

function subscribeTokenRefresh(cb) {
  subscribers.push(cb);
}

function onRefreshSuccess() {
  subscribers.forEach(cb => cb(/* success */));
  subscribers = []; // ✅ Clear after firing
}

function onRefreshFailure() {
  subscribers.forEach(cb => cb(new Error("Refresh failed"), null));
  subscribers = []; // ✅ Clear on failure too
}

// The backend's 403 for an expired / invalid access token; only that one is worth a refresh.
const INVALID_TOKEN_DETAIL = "Could not validate credentials";

// A blob request (file download) gets its JSON error body as a Blob too.
async function errorDetail(response) {
  let data = response && response.data;
  if (typeof Blob !== "undefined" && data instanceof Blob) {
    try {
      data = JSON.parse(await data.text());
    } catch (e) {
      data = null;
    }
  }
  return data && typeof data === "object" ? data.detail : undefined;
}

export function interceptorsResponse() {
  axios.interceptors.response.use(
    response => Promise.resolve(response),
    async function (error) {
      const { status } = error.response || {};
      const detail = await errorDetail(error.response);
      const { token, code } = detail && typeof detail === "object" ? detail : {};
      const originalRequest = error.config || {};

      // An admin switched the whole app to another database (modules/dbSwitch.js): every token
      // issued before it is refused, refresh tokens too - so before the refresh-token bail-out,
      // and never a refresh. The admin's own switch screen handles its own answers.
      if (status === 401 && code === "DB_SWITCHED") {
        if (!store.getters.isOwnDbSwitchRunning) store.dispatch("dbSwitchedLogout");
        return Promise.reject(error);
      }

      // That switch is running (15-45 s): show the "switching database" overlay, which polls.
      if (status === 503 && code === "MAINTENANCE") {
        store.dispatch("startDbSwitchMaintenance");
        return Promise.reject(error);
      }

      // ✅ Bail out immediately for refresh-token endpoint to avoid loop
      if ((originalRequest.url || "").includes("/login/refresh-token")) {
        return Promise.reject(error);
      }

      // The candidate shares a limited / anonymised profile with this organisation. Not an expired
      // token, so no refresh - retrying would only get the same 403 again.
      if (status === 403 && code === "DOCUMENTS_NOT_SHARED") {
        store.dispatch("showToast", {
          class: "bg-danger text-white",
          message: "This candidate doesn't share their documents with your organisation",
        });
        return Promise.reject(error);
      }

      // Any other 403 with its own message is a permission answer ("You can only use your own
      // scratchpad", "Not allowed for this customer"); a fresh token wouldn't change it.
      if (status === 403 && detail !== undefined && detail !== INVALID_TOKEN_DETAIL) {
        return Promise.reject(error);
      }

      if (status === 403) {
        if (!isRefreshing) {
          isRefreshing = true;

          store
            .dispatch("refreshToken")
            .then(({ status }) => {
              if (status === 200 || status === 204) {
                onRefreshSuccess(); // ✅ Notify + clear subscribers
              }
            })
            .catch(refreshErr => {
              const { response: { status: refreshStatus } = {} } = refreshErr || {};
              if (refreshStatus === 403) {
                store.dispatch("logout");
              }
              onRefreshFailure(); // ✅ Reject + clear subscribers on any failure
            })
            .finally(() => {
              isRefreshing = false; // ✅ Always reset flag
            });
        }

        // ✅ Queue the failed request and retry after refresh
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh(err => {
            if (err) return reject(err); // ✅ Properly reject queued requests on failure
            resolve(axios(originalRequest));
          });
        });
      }

      if (status === 401 && token) {
        store.dispatch("redirectToReset", token);
        return;
      }

      if (status === 419) {
        store.dispatch("forceLogout");
        store.dispatch("clearAllToast");
        store.dispatch("showToast", {
          class: "bg-danger text-white",
          message: "You have been logged out, as a new login session for the same credentials has been detected!",
          autohide: 7000
        });
        return;
      }

      return Promise.reject(error);
    }
  );
}